import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useEventId } from "../EventContext";
import { displayElapsed, finishDisplay, durationToMs } from "../utils/finishTime";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  onSnapshot,
  updateDoc,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMars,
  faVenus,
  faEdit,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import { InputField, Button, PageLoader, useAuth } from "@vionixasia/shared-components";



const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Penalty as a human duration: "+5400s penalty" makes an official do the
 * arithmetic mid-protest to find out it means an hour and a half.
 */
const formatPenalty = (secs) => {
  const n = Math.abs(parseInt(secs, 10) || 0);
  if (!n) return "0s";
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = n % 60;
  return [h && `${h}h`, m && `${m}m`, s && `${s}s`].filter(Boolean).join(" ");
};

const timeToSeconds = (time) => {
  if (!time || time.includes("COT")) return Infinity;
  const cleanTime = time.replace("COT (", "").replace(")", "");
  const [h, m, s] = cleanTime.split(":").map(Number);
  return h * 3600 + m * 60 + s;
};

const formatGender = (gender) => {
  if (!gender || typeof gender !== "string") return "-";
  const normalizedGender = gender.toLowerCase();
  if (normalizedGender === "male" || normalizedGender === "m")
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" /> Male
      </>
    );
  if (normalizedGender === "female" || normalizedGender === "f")
    return (
      <>
        <FontAwesomeIcon icon={faVenus} className="mr-1" /> Female
      </>
    );
  return "-";
};

const formatTime = (timestamp, isCOT = false) => {
  if (!timestamp) return { time: "-", isCOT };
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return { time: "-", isCOT };
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";
  const options = {
    timeZone: userTimeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };
  const timeStr = date.toLocaleTimeString("en-GB", options);
  return { time: timeStr, isCOT };
};

const formatTimeForEdit = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return "";
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";
  // Return full ISO-like string for datetime-local AND robustness
  const timeStr = moment(date).tz(userTimeZone).format("YYYY-MM-DDTHH:mm:ss");
  return timeStr;
};

const toISOFormat = (timeStr, eventDate, originalTime) => {
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";

  if (!timeStr) return null;

  // If it's already a complete ISO string with timezone (e.g. ends with Z), just return it
  if (timeStr.endsWith("Z") || timeStr.match(/[+-]\d{2}:?\d{2}$/)) {
    const m = moment(timeStr);
    if (m.isValid()) return m.toISOString();
  }

  // Check if it is a full ISO-like string (YYYY-MM-DDTHH:mm:ss) from datetime-local input
  if (timeStr.includes("T")) {
       const m = moment.tz(timeStr, "YYYY-MM-DDTHH:mm:ss", userTimeZone);
       if (m.isValid()) return m.toISOString();
  }

  // Fallback for legacy HH:mm:ss input (if any)
  if (moment(timeStr, "HH:mm:ss", true).isValid()) {
      const dateToUse = originalTime
        ? moment(originalTime).tz(userTimeZone).format("YYYY-MM-DD")
        : eventDate;
      return moment
        .tz(`${dateToUse} ${timeStr}`, "YYYY-MM-DD HH:mm:ss", userTimeZone)
        .toISOString();
  }
  
  return null;
};

const getHeroDetails = (selectedCategory, categories, eventName) => {
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";
  const now = moment().tz(userTimeZone);
  if (selectedCategory === "") {
    const anyOngoing = categories.some((cat) => {
      const cutOff = cat.cutOffTime?.toDate
        ? moment(cat.cutOffTime.toDate()).tz(userTimeZone)
        : moment(cat.cutOffTime).tz(userTimeZone);
      return cutOff.isValid() && now.isBefore(cutOff);
    });
    return {
      title: eventName,
      dateTime: "Multiple Dates",
      startTime: null,
      cutOffTime: null,
      isCOT: !anyOngoing,
    };
  }

  const category = categories.find((cat) => cat.id === selectedCategory);
  if (!category || !category.startTime) {
    return {
      title: "Unknown Category",
      dateTime: "Unknown Date",
      startTime: null,
      cutOffTime: null,
      isCOT: false,
    };
  }

  const startTime = category.startTime.toDate
    ? moment(category.startTime.toDate()).tz(userTimeZone)
    : moment(category.startTime).tz(userTimeZone);
  const cutOffTime = category.cutOffTime.toDate
    ? moment(category.cutOffTime.toDate()).tz(userTimeZone)
    : moment(category.cutOffTime).tz(userTimeZone);

  if (!startTime.isValid()) {
    return {
      title: category.name?.trim() || "Unknown Category",
      dateTime: "Invalid Date",
      startTime: null,
      cutOffTime: null,
      isCOT: false,
    };
  }

  const isCOT = cutOffTime.isValid() && now.isAfter(cutOffTime);

  return {
    title: category.name?.trim() || "Unknown Category",
    dateTime: startTime.format("DD MMM YYYY [at] HH:mm"),
    startTime: category.startTime,
    cutOffTime: category.cutOffTime,
    isCOT,
  };
};

const Stopwatch = ({
  startTime,
  cutOffTime,
  eventStatus,
  categoryName,
  isCOT,
}) => {
  const [elapsedTime, setElapsedTime] = useState("00:00:00");
  const [isCOTState, setIsCOTState] = useState(isCOT || false);
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";

  useEffect(() => {
    if (!startTime || eventStatus !== "ongoing") {
      setIsCOTState(isCOT || false);
      return;
    }

    const start = startTime.toDate
      ? moment(startTime.toDate()).tz(userTimeZone)
      : moment(startTime).tz(userTimeZone);
    const cutOff = cutOffTime.toDate
      ? moment(cutOffTime.toDate()).tz(userTimeZone)
      : moment(cutOffTime).tz(userTimeZone);

    if (!start.isValid()) {
      setIsCOTState(isCOT || false);
      return;
    }

    const updateTime = () => {
      const now = moment().tz(userTimeZone);
      const duration = moment.duration(now.diff(start));
      const hours = Math.floor(duration.asHours()).toString().padStart(2, "0");
      const minutes = duration.minutes().toString().padStart(2, "0");
      const seconds = duration.seconds().toString().padStart(2, "0");
      setElapsedTime(`${hours}:${minutes}:${seconds}`);
      if (cutOff.isValid() && now.isAfter(cutOff)) {
        setIsCOTState(true);
      } else {
        setIsCOTState(false);
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [startTime, cutOffTime, eventStatus, isCOT, userTimeZone]);

  if (!startTime || eventStatus !== "ongoing") {
    if (isCOTState) {
      return (
        <div className="absolute bottom-2 left-2 md:bottom-4 md:left-4 flex flex-row items-center gap-2 z-10">
          <span className="text-md md:text-lg font-oxanium text-center text-white drop-shadow-md">
            FINISHED {categoryName || "All Categories"}
          </span>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="absolute bottom-2 left-2 md:bottom-4 md:left-4 flex flex-row items-center gap-2 z-10">
      <span className="text-md md:text-lg font-oxanium text-center text-white drop-shadow-md">
        {isCOTState
          ? `FINISHED ${categoryName || "Unknown Category"}`
          : `ONGOING ${categoryName || "Unknown Category"} ${elapsedTime}`}
      </span>
    </div>
  );
};

const Hero = ({ selectedCategory, categories, eventName, eventStatus }) => {
  const { title, dateTime, startTime, cutOffTime, isCOT } = getHeroDetails(
    selectedCategory,
    categories,
    eventName
  );

  return (
    <div
      className="relative w-full h-[50vh] md:h-[60vh] bg-cover bg-center mb-8 rounded-xl overflow-hidden"
      style={{ backgroundImage: "url('/bg/smm.webp')" }}
    >
      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-slate-50/10 flex flex-col items-center justify-center text-white px-4">
        <img
          src="https://images.squarespace-cdn.com/content/v1/683c3e14f1dc4f043e729ba0/3156d7d9-fc4c-453f-9bcf-e2a11daa629c/Logo_Mountain+Marathon+Festival.png?format=1500w"
          alt="Mountain Marathon Festival Logo"
          className="w-60 sm:w-60 md:w-60 lg:w-60 xl:w-80 drop-shadow-lg opacity-95 object-contain mb-4"
        />
        <h2 className="text-3xl md:text-5xl font-bold font-oxanium uppercase tracking-wider text-center">
          {title}
        </h2>
        <p className="text-md md:text-xl md:mt-2 font-oxanium text-center text-white">
          {dateTime}
        </p>
        <Stopwatch
          startTime={startTime}
          cutOffTime={cutOffTime}
          eventStatus={eventStatus}
          categoryName={title}
          isCOT={isCOT}
        />
      </div>
    </div>
  );
};



const Top3Table = ({ title, results, userTimeZone }) => (
  <div className="w-full md:w-[calc(50%-0.5rem)] bg-white rounded-xl border border-slate-200 overflow-x-auto">
    <h2 className="text-lg font-semibold text-slate-900 mb-4 px-4 pt-4">
      {title}
    </h2>
    {/* table-fixed on mobile so the rank column holds its declared width. */}
    <table className="min-w-full table-fixed md:table-auto border-collapse">
      <thead className="bg-slate-50 border-y border-slate-200">
        <tr>
          {/* Mobile geometry matches this page's own overall card, which is p-3 with a
              gap-3 flex row and a 28px rank pill: 12 + 28 + 12 = 52px to the name.
              w-13 does not exist in Tailwind, so the 52px is spelled out. */}
          <th className="w-[52px] pl-3 pr-0 md:w-[60px] md:px-2 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
             {/* Use Rank instead of # for clarity in Top 3 */}
            Rank
          </th>
          <th className="bg-slate-50 border-y border-slate-200 px-0 md:px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider md:max-w-[150px]">
            Name
          </th>
          <th className="w-24 pr-3 pl-0 md:w-auto md:px-4 py-3 text-right md:text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
            Finish
          </th>
        </tr>
      </thead>
      <tbody>
        {results.length === 0 ? (
          <tr>
            <td
              colSpan={3}
              className="px-4 py-2 text-center text-sm text-slate-600"
            >
              No data available
            </td>
          </tr>
        ) : (
          results.map((result, index) => {
             // Rule: Top 3 Gender HANYA DURASI (Only Duration)
             // We calculated finishDuration (elapsed) in calculateRank for both finishers and loopers.
             let displayTime = result.finishDuration || "-";
             
             return (
            <tr
              key={`top3-${result.bib || index}`}
              className="hover:-translate-y-[2px] transition-transform shadow-sm group"
            >
              <td className="w-[52px] pl-3 pr-0 md:w-[60px] md:px-2 py-3 text-sm text-slate-600 text-center">
                {result.genderRank > 0 ? result.genderRank : "-"}
              </td>
              <td className="px-0 md:px-4 py-3 text-sm text-slate-600 uppercase max-w-0 md:max-w-[150px] truncate">
                {capitalizeWords(result.name) || "-"}
              </td>
              <td className="w-24 pr-3 pl-0 md:w-auto md:px-4 py-3 text-right md:text-left text-sm text-slate-600 font-bold">
                {displayTime}
              </td>
            </tr>
          );
          })
        )}
      </tbody>
    </table>
  </div>
);

export const ResultsEditor = () => {
  const { realEventId: EVENT_ID } = useEventId();
  const { getIdToken } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({
    raceResults: [],
    rankedResults: [],
    top3Male: [],
    top3Female: [],
    loading: true,
    totalResults: 0,
    selectedCategory: "",
    categories: [],
    eventName: "SM Marathon",
    eventStatus: "ongoing",
    eventDate: "2025-10-21",
    error: null,
    participantsByBib: {},
    editingRunner: null,
    isDeleting: false,
    editForm: { checkpoints: {} },
    editError: null,
    editSuccess: null,
    deleteError: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  // "all" | "penalty" | "status" | "nofinish" — narrows the table to the rows an
  // official actually needs to revisit.
  const [flagFilter, setFlagFilter] = useState("all");
  const [bibNumberInput, setBibNumberInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const debounceRef = useRef(null);
  const db = getFirestore();
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";

  const tableCheckpoints = useMemo(() => {
    if (state.selectedCategory && state.selectedCategory !== "") {
      const selectedCategory = state.categories.find(
        (cat) => cat.id === state.selectedCategory
      );
      
      // Handle Looping Category
      if (selectedCategory?.isLooping) {
        // No per-lap columns. Nobody compares runner A's lap 40 against runner
        // B's lap 40, which is the only thing a column per lap is good for —
        // and at 105, 250 or ~500 laps the table stopped being readable at all.
        // The table's job is to show WHO needs attention; the crossings
        // themselves are corrected in the modal.
        const loops = [];
        // Add Finish/Values column if needed, or just relying on loops?
        // Usually L(Last) is finish. But let's add 'finish' explicitly if the data uses it separate?
        // ResultsLoop uses loopX columns.
        // Let's stick to loop columns.
        return loops;
      }

      if (
        selectedCategory &&
        selectedCategory.checkpoints &&
        Array.isArray(selectedCategory.checkpoints)
      ) {
        // Filter out startfinish and finish — they get a dedicated FINISH column at the right
        const checkpoints = selectedCategory.checkpoints.filter(
          (cp) => cp.locationId && cp.name && cp.type !== "startfinish" && cp.locationId !== "finish"
        );
        return checkpoints;
      }
      return [];
    } else {
      // Dynamic: Mix of sorting.
      // If ANY category is looping, we might have a display issue mixing CP and Loops.
      // But typically we show "CP1/L1" etc?
      // For now, keep existing logic for "All Categories" but maybe enhance?
      // User likely selects a category to edit.
      
      const sortedCategories = [...state.categories].sort(
        (a, b) => (b.checkpoints?.length || 0) - (a.checkpoints?.length || 0)
      );

      const uniqueCheckpointsMap = new Map();
      sortedCategories.forEach((cat) => {
        // Lap categories contribute no columns here either — this is the same
        // per-lap expansion removed above, and it was still running in the "All
        // Categories" view, where 100K alone added 250 columns. Lap counts are
        // shown in the dedicated Laps column.
        if (cat.isLooping) {
          return;
        } else if (cat.checkpoints && Array.isArray(cat.checkpoints)) {
          cat.checkpoints.forEach((cp) => {
            // Skip startfinish and finish — they go to the dedicated FINISH column
            if (cp.locationId && cp.name && cp.type !== "startfinish" && cp.locationId !== "finish") {
              if (!uniqueCheckpointsMap.has(cp.locationId)) {
                uniqueCheckpointsMap.set(cp.locationId, cp);
              }
            }
          });
        }
      });

      return Array.from(uniqueCheckpointsMap.values());
    }
  }, [state.categories, state.selectedCategory]);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    const eventRef = doc(db, "event_timing", EVENT_ID);
    const unsubscribeEvent = onSnapshot(
      eventRef,
      (doc) => {
        if (doc.exists()) {
          const eventData = doc.data();
          setState((prev) => ({
            ...prev,
            eventName: eventData.eventName || "SM Marathon",
            eventStatus: eventData.status || "ongoing",
            eventDate: eventData.date || "2025-10-21",
            categories:
              eventData.categories && Array.isArray(eventData.categories)
                ? eventData.categories.map((cat) => ({
                    ...cat,
                    // A buzzer-timed category has no start or cut-off: keep them null. new Date(null)
                    // is 1 Jan 1970, which the hero printed as the date and read as
                    // an expired cut-off ("FINISHED" before the race).
                    startTime: !cat.startTime
                      ? null
                      : cat.startTime.toDate
                      ? cat.startTime
                      : new Date(cat.startTime),
                    cutOffTime: !cat.cutOffTime
                      ? null
                      : cat.cutOffTime.toDate
                      ? cat.cutOffTime
                      : new Date(cat.cutOffTime),
                  }))
                : [],
            loading: false,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            error: "Event not found. Please contact the administrator.",
            loading: false,
          }));
        }
      },
      (error) => {
        setState((prev) => ({
          ...prev,
          error: `Failed to load event data: ${error.message}`,
          loading: false,
        }));
      }
    );

    const resultsRef = collection(db, "event_timing", EVENT_ID, "results");
    const unsubscribeResults = onSnapshot(
      resultsRef,
      (snapshot) => {
        // Summarise laps at load instead of carrying them. 351 runners on a
        // 24-hour track hold 33,425 lap entries — 2.15 MB that React re-renders
        // on every snapshot, which is what froze the page. The table only needs
        // the count and whether the sequence has holes; the crossings
        // themselves are fetched per runner when the modal opens.
        const resultsList = snapshot.docs.map((doc) => {
          const data = doc.data();
          const checkpoints = {};
          let lapHighest = 0;
          let lapStored = 0;
          let lapAdjusted = false;
          let lapLastTime = null;

          Object.entries(data.checkpoints || {}).forEach(([cpId, cp]) => {
            if (!cp || typeof cp !== "object") return;
            // A lap with no time is not a lap. Earlier saves stored a cleared
            // lap as { time: null } rather than removing the key, so the count
            // still read 500 after L500 had been deleted.
            const loopKeys = Object.keys(cp).filter((k) => /^loop\d+$/.test(k) && cp[k]?.time);
            if (!loopKeys.length) {
              checkpoints[cpId] = cp;
              return;
            }
            // Keep the non-lap fields of this checkpoint (startCrossing etc.),
            // drop the lap entries themselves.
            const kept = {};
            Object.entries(cp).forEach(([k, v]) => {
              if (!/^loop\d+$/.test(k)) kept[k] = v;
            });
            checkpoints[cpId] = kept;
            lapStored += loopKeys.length;
            loopKeys.forEach((k) => {
              const n = parseInt(k.slice(4), 10);
              if (n > lapHighest) {
                lapHighest = n;
                lapLastTime = cp[k]?.time || null;
              }
              if (cp[k]?.manualAdjust) lapAdjusted = true;
            });
          });

          return {
            ...data,
            checkpoints,
            id: doc.id,
            _lapHighest: lapHighest,
            _lapGaps: Math.max(0, lapHighest - lapStored),
            _lapAdjusted: lapAdjusted,
            _lapLastTime: lapLastTime,
          };
        });
        setState((prev) => ({
          ...prev,
          raceResults: resultsList,
          totalResults: snapshot.size,
          loading: false,
        }));
      },
      (error) => {
        setState((prev) => ({
          ...prev,
          error: `Failed to load race results: ${error.message}`,
          loading: false,
        }));
      }
    );

    // Participants are static during a race (they only change on CSV upload), so
    // a realtime listener over 683 documents buys nothing. One cached fetch does.
    let participantsAlive = true;
    fetch(`${import.meta.env.VITE_API_BASE_URL}/api/timing/participants?eventId=${encodeURIComponent(EVENT_ID)}`)
      .then((r) => (r.ok ? r.json() : { participants: [] }))
      .then((d) => {
        if (!participantsAlive) return;
        const map = {};
        (d.participants || []).forEach((p) => {
          map[String(p.bib)] = p;
        });
        setState((prev) => ({ ...prev, participantsByBib: map }));
      })
      .catch(() => {
        // Non-fatal: results still render, the Code column just stays empty.
      });

    return () => {
      unsubscribeEvent();
      unsubscribeResults();
      participantsAlive = false;
    };
  }, [db]);

  const calculateRank = useCallback(
    (results, categoryId) => {
      const rankedResults = results.map((runner) => {
        // A buzzer-timed result carries no checkpoints map at all — there are
        // no mats. Rejecting on that discarded its stored duration and rank.
        if (!runner?.bib || !runner?.categoryId || (!runner?.checkpoints && !runner?.finishDuration)) {
          return {
            ...runner,
            overallRank: -1,
            genderRank: -1,
            isCOT: false,
            checkpointCount: 0,
            finishDuration: null,
          };
        }

        const category = state.categories.find(
          (cat) => cat.id === runner.categoryId
        );
        let isCOT = false;
        let finishTime = null;
        // Keep a duration the server already computed: the checkpoint
        // arithmetic below never runs for a buzzer-timed result, so starting
        // from null wiped a perfectly good time.
        let finishDuration = runner.finishDuration || null;

        // A buzzer-timed result has no mats, so it holds no checkpoints at all.
        // Counting zero would strip its rank below; the recorded duration is the
        // evidence that the athlete ran, so it counts as one.
        const checkpointCount = runner.finishDuration
          ? Math.max(Object.keys(runner.checkpoints || {}).length, 1)
          : Object.keys(runner.checkpoints || {}).length;

        const finishCheckpoint = runner.checkpoints?.finish?.time;
        if (finishCheckpoint) {
          let finishMoment = moment(finishCheckpoint).tz(userTimeZone);
          // Apply penalty if any
          const penaltySecs = runner.penaltySeconds || 0;
          if (penaltySecs > 0) {
            finishMoment = finishMoment.clone().add(penaltySecs, 'seconds');
          }
          if (finishMoment.isValid()) {
            finishTime = formatTime(finishCheckpoint, false).time;
            if (category?.startTime) {
              const startMoment = moment(
                category.startTime.toDate
                  ? category.startTime.toDate()
                  : category.startTime
              ).tz(userTimeZone);
              if (startMoment.isValid() && finishMoment.isAfter(startMoment)) {
                const duration = moment.duration(
                  finishMoment.diff(startMoment)
                );
                const hours = Math.floor(duration.asHours())
                  .toString()
                  .padStart(2, "0");
                const minutes = duration.minutes().toString().padStart(2, "0");
                const seconds = duration.seconds().toString().padStart(2, "0");
                finishDuration = `${hours}:${minutes}:${seconds}`;
              } else {
                finishDuration = "-";
              }
            }
            if (category?.cutOffTime) {
              const cutOffMoment = moment(
                category.cutOffTime.toDate
                  ? category.cutOffTime.toDate()
                  : category.cutOffTime
              ).tz(userTimeZone);
              isCOT =
                cutOffMoment.isValid() && finishMoment.isAfter(cutOffMoment);
              if (isCOT) finishTime = `COT (${finishTime})`;
            }
          }
        }

        // Calculate Loops and duration for Looping Categories
        let completedLoops = 0;
        let lastLoopTime = 0;
        let calculatedDuration = finishDuration;
        let calculatedTime = finishTime;

        if (category?.isLooping) {
            // Use the summary captured at load. Re-scanning every lap here ran
            // over 33,425 entries on each pass; the count and the last crossing
            // are all this needs.
            if (runner._lapHighest > 0) {
                completedLoops = runner._lapHighest;
                const t = runner._lapLastTime;
                lastLoopTime = t ? (t.toDate ? t.toDate().getTime() : new Date(t).getTime()) : 0;
                
                // If not finished (no finish checkpoint), use last loop for Duration calculation
                if (!finishDuration || finishDuration === "-") {
                     const lastMoment = moment(lastLoopTime).tz(userTimeZone);
                     
                     // Format Time (HH:mm:ss)
                     calculatedTime = lastMoment.format("HH:mm:ss");

                     if (category?.startTime) {
                        const startMoment = moment(
                            category.startTime.toDate
                            ? category.startTime.toDate()
                            : category.startTime
                        ).tz(userTimeZone);
                        
                        if (startMoment.isValid() && lastMoment.isAfter(startMoment)) {
                             const dur = moment.duration(lastMoment.diff(startMoment));
                             const h = Math.floor(dur.asHours()).toString().padStart(2, "0");
                             const m = dur.minutes().toString().padStart(2, "0");
                             const s = dur.seconds().toString().padStart(2, "0");
                             calculatedDuration = `${h}:${m}:${s}`;
                        }
                     }
                }
            }
        }

        return {
          ...runner,
          overallRank: -1,
          genderRank: -1,
          isCOT,
          finishTime: calculatedTime,
          finishDuration: calculatedDuration,
          checkpointCount,
          completedLoops,
          lastLoopTime,
        };
      });

      const validRunners = rankedResults.filter(
        (r) => r.categoryId === categoryId && !r.isCOT && r.checkpointCount > 0 && r.status !== "DNF"
      );

      const sortedCategoryRunners = [...validRunners].sort((a, b) => {
        const category = state.categories.find(c => c.id === categoryId);
        
        // LOOPING SORT
        if (category?.isLooping) {
            const loopsA = a.completedLoops || 0;
            const loopsB = b.completedLoops || 0;
            if (loopsA !== loopsB) return loopsB - loopsA;
            
            const getLastTime = (r, loopNum) => {
                 if (loopNum <= 0) return Infinity;
                 const key = `loop${loopNum}`;
                 if (r.checkpoints) {
                     for (const cp of Object.values(r.checkpoints)) {
                         if (cp[key]?.time) {
                             return cp[key].time.toDate ? cp[key].time.toDate().getTime() : new Date(cp[key].time).getTime();
                         }
                     }
                 }
                 return Infinity;
            };
            return getLastTime(a, loopsA) - getLastTime(b, loopsB);
        }

        // STANDARD SORT
        // Check if runners have finished
        const aHasFinish = a.checkpoints?.finish?.time;
        const bHasFinish = b.checkpoints?.finish?.time;
        
        // Priority 1: Finished runners always rank higher than non-finished
        if (aHasFinish && !bHasFinish) return -1;
        if (!aHasFinish && bHasFinish) return 1;
        
        // Priority 2: Both finished - sort by finish time (faster = better)
        if (aHasFinish && bHasFinish) {
          let aMs = new Date(typeof aHasFinish === 'string' ? aHasFinish : aHasFinish.toDate ? aHasFinish.toDate() : aHasFinish).getTime();
          let bMs = new Date(typeof bHasFinish === 'string' ? bHasFinish : bHasFinish.toDate ? bHasFinish.toDate() : bHasFinish).getTime();
          const aPenalty = a.penaltySeconds || 0;
          const bPenalty = b.penaltySeconds || 0;
          if (aPenalty > 0) aMs += aPenalty * 1000;
          if (bPenalty > 0) bMs += bPenalty * 1000;
          return aMs - bMs;
        }
        
        // Priority 3: Neither finished - sort by checkpoint count (more = better)
        if (a.checkpointCount !== b.checkpointCount) {
          return b.checkpointCount - a.checkpointCount;
        }
        
        // Priority 4: Same checkpoint count - sort by latest checkpoint time (earlier = better)
        const getLatestCpTime = (runner) => {
          const checkpoints = Object.keys(runner.checkpoints || {});
          if (!checkpoints.length) return Infinity;
          const latestCp = checkpoints
            .filter((cp) => runner.checkpoints[cp]?.time)
            .sort((cp1, cp2) => {
              const time1 = new Date(runner.checkpoints[cp1].time).getTime();
              const time2 = new Date(runner.checkpoints[cp2].time).getTime();
              return time2 - time1;
            })[0];
          return latestCp
            ? new Date(runner.checkpoints[latestCp].time).getTime()
            : Infinity;
        };

        const aLatestTime = getLatestCpTime(a);
        const bLatestTime = getLatestCpTime(b);
        return aLatestTime - bLatestTime;
      });

      const updatedResults = rankedResults.map((runner) => {
        if (runner.isCOT || runner.checkpointCount === 0) {
          return { ...runner, overallRank: -1, genderRank: -1 };
        }

        const overallRank =
          sortedCategoryRunners.findIndex((r) => r.bib === runner.bib) + 1;
        const genderFiltered = sortedCategoryRunners.filter(
          (r) => r.gender.toLowerCase() === runner.gender.toLowerCase()
        );
        const genderRank =
          genderFiltered.findIndex((r) => r.bib === runner.bib) + 1;

        return {
          ...runner,
          overallRank,
          genderRank,
        };
      });

      const top3Male = updatedResults
        .filter(
          (r) =>
            r.gender.toLowerCase() === "male" &&
            r.categoryId === categoryId &&
            !r.isCOT &&
            r.checkpointCount > 0 &&
            r.status !== "DNF"
        )
        .sort((a, b) => {
           const category = state.categories.find(c => c.id === categoryId);
           if (category?.isLooping) {
                const loopsA = a.completedLoops || 0;
                const loopsB = b.completedLoops || 0;
                if (loopsA !== loopsB) return loopsB - loopsA;
                
                const getLastTime = (r, loopNum) => {
                     if (loopNum <= 0) return Infinity;
                     const key = `loop${loopNum}`;
                     if (r.checkpoints) {
                         for (const cp of Object.values(r.checkpoints)) {
                             if (cp[key]?.time) {
                                 return cp[key].time.toDate ? cp[key].time.toDate().getTime() : new Date(cp[key].time).getTime();
                             }
                         }
                     }
                     return Infinity;
                };
                return getLastTime(a, loopsA) - getLastTime(b, loopsB);
           }

          const aHasFinish = a.checkpoints?.finish?.time;
          const bHasFinish = b.checkpoints?.finish?.time;
          if (aHasFinish && !bHasFinish) return -1;
          if (!aHasFinish && bHasFinish) return 1;
          if (aHasFinish && bHasFinish) {
            const aMs = new Date(typeof aHasFinish === 'string' ? aHasFinish : aHasFinish.toDate ? aHasFinish.toDate() : aHasFinish).getTime();
            const bMs = new Date(typeof bHasFinish === 'string' ? bHasFinish : bHasFinish.toDate ? bHasFinish.toDate() : bHasFinish).getTime();
            return aMs - bMs;
          }
          if (a.checkpointCount !== b.checkpointCount)
            return b.checkpointCount - a.checkpointCount;
          const aLatestTime = Object.keys(a.checkpoints || {})
            .filter((cp) => a.checkpoints[cp]?.time)
            .sort((cp1, cp2) => {
              const time1 = new Date(a.checkpoints[cp1].time).getTime();
              const time2 = new Date(a.checkpoints[cp2].time).getTime();
              return time2 - time1;
            })[0];
          const bLatestTime = Object.keys(b.checkpoints || {})
            .filter((cp) => b.checkpoints[cp]?.time)
            .sort((cp1, cp2) => {
              const time1 = new Date(b.checkpoints[cp1].time).getTime();
              const time2 = new Date(b.checkpoints[cp2].time).getTime();
              return time2 - time1;
            })[0];
          return (
            (aLatestTime
              ? new Date(a.checkpoints[aLatestTime].time).getTime()
              : Infinity) -
            (bLatestTime
              ? new Date(b.checkpoints[bLatestTime].time).getTime()
              : Infinity)
          );
        })
        .slice(0, 3);

      const top3Female = updatedResults
        .filter(
          (r) =>
            r.gender.toLowerCase() === "female" &&
            r.categoryId === categoryId &&
            !r.isCOT &&
            r.checkpointCount > 0 &&
            r.status !== "DNF"
        )
        .sort((a, b) => {
           const category = state.categories.find(c => c.id === categoryId);
           if (category?.isLooping) {
                const loopsA = a.completedLoops || 0;
                const loopsB = b.completedLoops || 0;
                if (loopsA !== loopsB) return loopsB - loopsA;
                
                const getLastTime = (r, loopNum) => {
                     if (loopNum <= 0) return Infinity;
                     const key = `loop${loopNum}`;
                     if (r.checkpoints) {
                         for (const cp of Object.values(r.checkpoints)) {
                             if (cp[key]?.time) {
                                 return cp[key].time.toDate ? cp[key].time.toDate().getTime() : new Date(cp[key].time).getTime();
                             }
                         }
                     }
                     return Infinity;
                };
                return getLastTime(a, loopsA) - getLastTime(b, loopsB);
           }

          const aHasFinish = a.checkpoints?.finish?.time;
          const bHasFinish = b.checkpoints?.finish?.time;
          if (aHasFinish && !bHasFinish) return -1;
          if (!aHasFinish && bHasFinish) return 1;
          if (aHasFinish && bHasFinish) {
            const aMs = new Date(typeof aHasFinish === 'string' ? aHasFinish : aHasFinish.toDate ? aHasFinish.toDate() : aHasFinish).getTime();
            const bMs = new Date(typeof bHasFinish === 'string' ? bHasFinish : bHasFinish.toDate ? bHasFinish.toDate() : bHasFinish).getTime();
            return aMs - bMs;
          }
          if (a.checkpointCount !== b.checkpointCount)
            return b.checkpointCount - a.checkpointCount;
          const aLatestTime = Object.keys(a.checkpoints || {})
            .filter((cp) => a.checkpoints[cp]?.time)
            .sort((cp1, cp2) => {
              const time1 = new Date(a.checkpoints[cp1].time).getTime();
              const time2 = new Date(a.checkpoints[cp2].time).getTime();
              return time2 - time1;
            })[0];
          const bLatestTime = Object.keys(b.checkpoints || {})
            .filter((cp) => b.checkpoints[cp]?.time)
            .sort((cp1, cp2) => {
              const time1 = new Date(b.checkpoints[cp1].time).getTime();
              const time2 = new Date(b.checkpoints[cp2].time).getTime();
              return time2 - time1;
            })[0];
          return (
            (aLatestTime
              ? new Date(a.checkpoints[aLatestTime].time).getTime()
              : Infinity) -
            (bLatestTime
              ? new Date(b.checkpoints[bLatestTime].time).getTime()
              : Infinity)
          );
        })
        .slice(0, 3);

      return {
        rankedResults: updatedResults,
        top3Male,
        top3Female,
      };
    },
    [state.categories, userTimeZone]
  );

  useEffect(() => {
    if (state.raceResults.length > 0 && !state.loading) {
      const { rankedResults, top3Male, top3Female } = calculateRank(
        state.raceResults,
        state.selectedCategory
      );
      setState((prev) => ({
        ...prev,
        rankedResults,
        top3Male,
        top3Female,
      }));
    }
  }, [state.raceResults, state.selectedCategory, state.loading, calculateRank]);

  const handleCategoryChange = useCallback(
    (categoryId) => {
      if (categoryId === state.selectedCategory) return;
      setState((prev) => ({ ...prev, selectedCategory: categoryId }));
      setSearchQuery("");
      setBibNumberInput("");
    },
    [state.selectedCategory]
  );

  const handleInputChange = useCallback((event) => {
    const input = event.target.value;
    setBibNumberInput(input);
    setIsTyping(true);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setSearchQuery(input);
      setIsTyping(false);
    }, 500);
  }, []);

  // Whether the selected category is lap-based, which decides if the Laps
  // column appears at all.
  const isLoopCategory = React.useMemo(() => {
    // "All Categories" shows every runner at once, so the Laps column is needed
    // whenever any category is lap-based — not only when one is selected.
    if (!state.selectedCategory) {
      return state.categories.some((c) => c.isLooping);
    }
    const c = state.categories.find((x) => x.id === state.selectedCategory);
    return !!c?.isLooping;
  }, [state.categories, state.selectedCategory]);

  const formatResults = useCallback(
    (runner) => {
      const category = state.categories.find(
        (cat) => cat.id === runner.categoryId
      );
      if (!category || !tableCheckpoints.length) {
        return tableCheckpoints.map(() => ({ time: "-", isCOT: false }));
      }

      return tableCheckpoints.map((cp) => {
        let checkpointData = runner.checkpoints?.[cp.locationId];

        // Robust Lookup for Loops:
        // If checkpointData is missing AND it's a loop column (starts with "loop"),
        // scan all checkpoints to see if any contain this loop key.
        if (!checkpointData && cp.locationId.startsWith("loop") && runner.checkpoints) {
             for (const existingCp of Object.values(runner.checkpoints)) {
                 if (existingCp?.[cp.locationId]) {
                     checkpointData = existingCp[cp.locationId];
                     break;
                 }
                 // Handle flat structure if needed, but usually it's nested or direct.
             }
        }

        // startfinish type: MYLAPS stores data under "finish" key, not cp.locationId
        if (!checkpointData && cp.type === "startfinish") {
          checkpointData = runner.checkpoints?.finish;
        }

        // For finish checkpoint, check if manually marked as DNF first
        if (cp.locationId === "finish" || cp.type === "startfinish") {
          // Check if runner is manually marked as DNF
          if (runner.status === "DNF") {
            return { time: "DNF", isCOT: false, isDNF: true };
          }
          
          // A buzzer-timed result has no mat reading at all — its finish IS the
          // recorded duration. This check ran before the finishDuration lookup
          // below, so the cell returned "-" for a runner whose time was stored.
          if (!checkpointData?.time && runner.finishDuration) {
            return { time: displayElapsed(runner.finishDuration, category), isCOT: false };
          }

          // No finish time
          if (!checkpointData?.time) {
            return { time: "-", isCOT: false };
          }
          
          // Has finish time - check for COT
          let isCOT = false;
          if (category?.cutOffTime) {
            const cutOffMoment = moment(
              category.cutOffTime.toDate
                ? category.cutOffTime.toDate()
                : category.cutOffTime
            ).tz(userTimeZone);
            const finishMoment = checkpointData.time.toDate 
              ? moment(checkpointData.time.toDate()).tz(userTimeZone)
              : moment(checkpointData.time).tz(userTimeZone);
            
            isCOT = cutOffMoment.isValid() && finishMoment.isValid() && finishMoment.isAfter(cutOffMoment);
          }
          const { time } = formatTime(checkpointData.time, isCOT);
          const displayTime = isCOT ? `COT (${time})` : time;
          // Show only the duration (matching Results page behaviour)
          const displayText = runner.finishDuration && !isNaN(runner.finishDuration.split(":")[0])
            ? runner.finishDuration.replace(/\.\d+$/, "")
            : displayTime;
          return { time: displayText, isCOT };
        }

        // Non-finish checkpoints
        if (!checkpointData?.time) {
          return { time: "-", isCOT: false };
        }

        const { time } = formatTime(checkpointData.time, false);
        return { time, isCOT: false };
      });
    },
    [state.categories, tableCheckpoints, userTimeZone]
  );

  const [sortConfig, setSortConfig] = useState({
    key: "overallRank",
    direction: "ascending",
  });

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Roster entries that have no result document yet. Until a chip crosses a mat
  // nothing writes a result, so these runners were invisible here — and an
  // invisible runner cannot be marked DNS, which meant a no-show's BIB could
  // only be stopped *after* a stray read had already published them. They are
  // synthesised as read-only rows carrying just enough shape for the table.
  const rosterOnlyRows = useMemo(() => {
    const withResults = new Set(state.rankedResults.map((r) => String(r.bib)));
    return Object.values(state.participantsByBib || {})
      .filter((p) => !withResults.has(String(p.bib)))
      .map((p) => ({
        id: String(p.bib),
        bib: String(p.bib),
        name: p.name || "",
        gender: p.gender || "",
        nationality: p.nationality || "",
        categoryId: p.categoryId,
        chipCode: p.chipCode || null,
        checkpoints: {},
        completedLoops: 0,
        overallRank: -1,
        genderRank: -1,
        status: p.dns ? "DNS" : null,
        // Marks the row as roster-only: it has no result doc, so edit and delete
        // have nothing to act on and the status control writes to the
        // participant instead.
        _rosterOnly: true,
        _dns: !!p.dns,
      }));
  }, [state.rankedResults, state.participantsByBib]);

  const filteredResults = useMemo(() => {
    let results = [...state.rankedResults, ...rosterOnlyRows].filter((runner) => {
      const matchesBIB =
        !searchQuery ||
        runner.bib?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesName =
        !searchQuery ||
        runner.name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        state.selectedCategory === "" ||
        state.selectedCategory === runner.categoryId;
      // Penalised runners are the ones an official revisits, and they were
      // unfindable: with 674 rows the only way to spot them was to scroll.
      const matchesFlag =
        flagFilter === "all" ||
        (flagFilter === "penalty" && (runner.penaltySeconds || 0) > 0) ||
        (flagFilter === "status" && ["DNF", "DNS", "DSQ"].includes((runner.status || "").toUpperCase())) ||
        (flagFilter === "nofinish" && !runner.checkpoints?.finish?.time && !runner.finishMatTime);
      return (matchesBIB || matchesName) && matchesCategory && matchesFlag;
    });

    results.sort((a, b) => {
      // Status runners (DNF/DNS/DSQ) should be at the very bottom
      const statusOrder = { DNF: 1, DNS: 2, DSQ: 3 };
      const aStatus = statusOrder[a.status] || 0;
      const bStatus = statusOrder[b.status] || 0;
      if (aStatus && !bStatus) return 1;
      if (!aStatus && bStatus) return -1;

      if (aStatus && bStatus) {
         if (aStatus !== bStatus) return aStatus - bStatus; // Order: DNF, DNS, DSQ
         
         // Same Status (e.g. both DNF): Sort by Loops Desc, then Time Asc
         const loopsA = a.completedLoops || 0;
         const loopsB = b.completedLoops || 0;
         if (loopsA !== loopsB) return loopsB - loopsA;
         
         const timeA = a.lastLoopTime || Infinity;
         const timeB = b.lastLoopTime || Infinity;
         return timeA - timeB;
      }
      
      // COT should be below finished but above DNF
      if (a.isCOT && !b.isCOT) return 1;
      if (!a.isCOT && b.isCOT) return -1;
      if (a.isCOT && b.isCOT) {
        return timeToSeconds(a.finishTime) - timeToSeconds(b.finishTime);
      }

      if (sortConfig.key === "gender") {
        const genderA = a.gender || "";
        const genderB = b.gender || "";
        if (genderA < genderB) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (genderA > genderB) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        const rankA = a.overallRank > 0 ? a.overallRank : Infinity;
        const rankB = b.overallRank > 0 ? b.overallRank : Infinity;
        if (rankA !== Infinity || rankB !== Infinity) {
            return rankA - rankB;
        }
        return timeToSeconds(a.finishDuration || a.finishTime) - timeToSeconds(b.finishDuration || b.finishTime);
      }

      const rankA = a.overallRank > 0 ? a.overallRank : Infinity;
      const rankB = b.overallRank > 0 ? b.overallRank : Infinity;
      if (rankA !== Infinity || rankB !== Infinity) {
          return sortConfig.direction === "ascending" ? rankA - rankB : rankB - rankA;
      }
      
      const timeA = timeToSeconds(a.finishDuration || a.finishTime);
      const timeB = timeToSeconds(b.finishDuration || b.finishTime);
      return sortConfig.direction === "ascending" ? timeA - timeB : timeB - timeA;
    });

    return results;
  }, [state.rankedResults, rosterOnlyRows, searchQuery, state.selectedCategory, sortConfig, flagFilter]);

  const getRunnerStatus = useCallback(
    (runner, category) => {
      const finishTime = runner.checkpoints?.finish?.time;
      if (finishTime && moment(finishTime).isValid()) {
        if (category?.cutOffTime) {
          const finishMoment = moment(finishTime).tz(userTimeZone);
          const cutOffMoment = moment(
            category.cutOffTime.toDate
              ? category.cutOffTime.toDate()
              : category.cutOffTime
          ).tz(userTimeZone);
          if (cutOffMoment.isValid() && finishMoment.isAfter(cutOffMoment)) {
            return "COT";
          }
        }
        return "FINISHED";
      }

      const hasCheckpoints = Object.keys(runner.checkpoints || {}).some(
        (cp) =>
          runner.checkpoints[cp]?.time &&
          moment(runner.checkpoints[cp].time).isValid()
      );
      if (hasCheckpoints) {
        return "ON RACE";
      }

      return "DNF";
    },
    [userTimeZone]
  );

  const startEditing = useCallback(
    async (runnerSummary) => {
      // The table row carries a lap summary, not the crossings — they were
      // stripped at load to keep 33k entries out of memory. Fetch the full
      // document now, for this one runner, so the modal can edit them.
      let runner = runnerSummary;
      try {
        const snap = await getDoc(doc(db, "event_timing", EVENT_ID, "results", String(runnerSummary.id ?? runnerSummary.bib)));
        if (snap.exists()) runner = { ...snap.data(), id: snap.id };
      } catch {
        // Fall back to the summary: better a modal without lap rows than none.
      }
      const formattedCheckpoints = {};
      Object.keys(runner.checkpoints || {}).forEach((locationId) => {
        // Copy top level checkpoint
        if (runner.checkpoints[locationId]?.time) {
             formattedCheckpoints[locationId] = {
               ...runner.checkpoints[locationId],
               time: formatTimeForEdit(runner.checkpoints[locationId].time),
             };
        }
        
        // Scan for nested loops within this checkpoint
        const cpData = runner.checkpoints[locationId];
        if (cpData) {
            Object.keys(cpData).forEach(key => {
                if (key.startsWith('loop') && cpData[key]?.time) {
                     formattedCheckpoints[key] = {
                         ...cpData[key],
                         time: formatTimeForEdit(cpData[key].time),
                         _parent: locationId // Track parent for saving
                     };
                }
            });
        }
      });

      setState((prev) => ({
        ...prev,
        editingRunner: runner,
        isDeleting: false,
        editForm: {
          checkpoints: formattedCheckpoints,
          name: runner.name || "",
          bib: String(runner.bib ?? ""),
          gender: (runner.gender || "").toLowerCase(),
          chipCode:
            runner.chipCode ||
            prev.participantsByBib?.[String(runner.bib)]?.chipCode ||
            "",
          penaltySeconds: runner.penaltySeconds || 0,
          // Metres past the last mat crossing when the clock stopped. Paced out
          // by an official, so it can only be typed in — no mat can report it.
          extraMeters:
            runner.extraMeters === null || runner.extraMeters === undefined
              ? ""
              : String(runner.extraMeters),
          // Buzzer-timed events have no mats, so there is no crossing instant to
          // edit — the elapsed time itself is the record, and it is edited here.
          recordedDuration: (runner.recordedDuration || runner.finishDuration || "").replace(/\.000$/, ""),
          lapCorrectionTime: moment().tz(userTimeZone).format("YYYY-MM-DDTHH:mm:ss"),
        },
        editError: null,
        editSuccess: null,
        deleteError: null,
      }));
    },
    [formatTimeForEdit]
  );

  const handleEditInputChange = useCallback((locationId, value) => {
    setState((prev) => ({
      ...prev,
      editForm: {
        ...prev.editForm,
        checkpoints: {
          ...prev.editForm.checkpoints,
          [locationId]: {
            ...prev.editForm.checkpoints[locationId],
            time: value,
          },
        },
      },
    }));
  }, []);

  const handleEditFieldChange = useCallback((field, value) => {
    setState((prev) => ({
      ...prev,
      editForm: { ...prev.editForm, [field]: value },
    }));
  }, []);

  const saveEdit = useCallback(async () => {
    const { editingRunner, editForm, categories, eventDate } = state;
    if (!editingRunner) return;

    const db = getFirestore();
    const docRef = doc(db, "event_timing", EVENT_ID, "results", editingRunner.id);

    const category = categories.find(
      (cat) => cat.id === editingRunner.categoryId
    );

    // Buzzer-timed events store an elapsed time and nothing else — there are no
    // crossings to rewrite, so the whole checkpoint pipeline below does not
    // apply. Save the duration and stop.
    if (category?.isBuzzerTimed) {
      const raw = String(editForm.recordedDuration || "").trim();
      if (raw && !/^\d{1,3}:\d{2}:\d{2}(\.\d{1,3})?$/.test(raw)) {
        setState((prev) => ({ ...prev, editError: "Time must be hh:mm:ss (e.g. 00:05:00)" }));
        return;
      }
      const normalised = raw ? (raw.includes(".") ? raw : `${raw}.000`) : null;
      try {
        await updateDoc(docRef, {
          recordedDuration: normalised,
          finishDuration: normalised,
          recordedDurationMs: normalised ? durationToMs(normalised) : null,
          name: editForm.name ?? editingRunner.name ?? "",
          gender: (editForm.gender || editingRunner.gender || "").toLowerCase(),
          updatedAt: new Date().toISOString(),
        });
        setState((prev) => ({
          ...prev,
          editSuccess: "Saved",
          editError: null,
          editingRunner: null,
        }));
      } catch (error) {
        setState((prev) => ({ ...prev, editError: error.message }));
      }
      return;
    }

    const updatedCheckpoints = { ...editForm.checkpoints };
    let hasInvalidTime = false;

    Object.keys(updatedCheckpoints).forEach((cpId) => {
      const timeStr = updatedCheckpoints[cpId].time;
      const originalTime = editingRunner.checkpoints?.[cpId]?.time;
      if (timeStr) {
        const isoTime = toISOFormat(timeStr, eventDate, originalTime);
        if (!isoTime || !moment(isoTime).isValid()) {
          hasInvalidTime = true;
        } else {
          updatedCheckpoints[cpId].time = isoTime;
        }
      } else if (/^loop\d+$/.test(cpId)) {
        // Clearing a lap means removing it. Keeping the key with time: null left
        // the lap in Firestore, so deleting L500 changed nothing — the count
        // still read 500 because the key was still there.
        delete updatedCheckpoints[cpId];
      } else {
        // If empty, it means explicitly cleared/deleted by user (since editForm starts with original)
        updatedCheckpoints[cpId].time = null;
      }
    });

    if (hasInvalidTime) {
      setState((prev) => ({
        ...prev,
        editError: "Invalid time format. Use HH:mm:ss.",
      }));
      return;
    }

    const validCheckpoints = category.checkpoints.map((cp) => cp.locationId);
    // startfinish type checkpoints store data under "start" and "finish" keys in Firestore
    const hasStartFinishCp = category.checkpoints.some((cp) => cp.type === "startfinish");
    const invalidCheckpoints = Object.keys(updatedCheckpoints).filter(
      (cpId) =>
        !validCheckpoints.includes(cpId) &&
        !cpId.startsWith("loop") &&
        !(hasStartFinishCp && (cpId === "start" || cpId === "finish"))
    );
    if (invalidCheckpoints.length > 0) {
      setState((prev) => ({
        ...prev,
        editError: `Invalid checkpoint(s) for category ${
          editingRunner.categoryId
        }: ${invalidCheckpoints.join(", ")}`,
      }));
      return;
    }

    const checkpointTimes = Object.entries(updatedCheckpoints)
      .filter(([_, cpData]) => cpData.time)
      .sort(
        (a, b) => moment(a[1].time).valueOf() - moment(b[1].time).valueOf()
      );
    for (let i = 1; i < checkpointTimes.length; i++) {
      if (
        moment(checkpointTimes[i][1].time).isBefore(
          moment(checkpointTimes[i - 1][1].time)
        )
      ) {
        setState((prev) => ({
          ...prev,
          editError: "Checkpoint times must be in sequence.",
        }));
        return;
      }
    }

    const lastCpId = validCheckpoints[validCheckpoints.length - 1];
    
    // Reconstruct nested structure for saving
    const finalCheckpoints = {};
    Object.keys(updatedCheckpoints).forEach(key => {
        // Skip loops here, they will be merged into parents
        if (key.startsWith('loop')) return;
        finalCheckpoints[key] = updatedCheckpoints[key];
    });
    
    // Find a default parent ID for loops if needed (e.g. for new loops added that don't have _parent)
    let defaultLoopParent = "cp1";
    Object.keys(updatedCheckpoints).forEach(key => {
        if (key.startsWith('loop') && updatedCheckpoints[key]._parent) {
            defaultLoopParent = updatedCheckpoints[key]._parent;
        }
    });

    // Merge loops into their parents
    Object.keys(updatedCheckpoints).forEach(key => {
        if (key.startsWith('loop')) {
             const loopData = { ...updatedCheckpoints[key] };
             const parentId = loopData._parent || defaultLoopParent; // Fallback to detected parent
             
             if (parentId) {
                 delete loopData._parent;
                 finalCheckpoints[parentId] = {
                     ...(finalCheckpoints[parentId] || {}),
                     [key]: loopData
                 };
             }
        }
    });

    let requestedLapCorrection = null;
    // Loop-count corrections are committed by the backend after this profile /
    // timestamp edit. The server updates individual or relay-team state and all
    // official timing metadata in one transaction.
    const editCat = state.categories.find((c) => c.id === editingRunner.categoryId);
    if (editCat?.isLooping && editForm.lapAdjustTo != null) {
      const ownHave = Object.values(finalCheckpoints).reduce((count, checkpoint) =>
        count + Object.keys(checkpoint || {}).filter((key) => /^loop\d+$/.test(key) && checkpoint[key]?.time).length, 0);
      const have = editCat.isRelay && editingRunner.teamId
        ? state.raceResults
            .filter((runner) => runner.categoryId === editingRunner.categoryId && runner.teamId === editingRunner.teamId)
            .reduce((count, runner) => count + (Number(runner._lapHighest) || Number(runner.completedLoops) || 0), 0)
        : ownHave;
      const want = Math.max(0, parseInt(editForm.lapAdjustTo, 10) || 0);
      if (want !== have) {
        requestedLapCorrection = { have, want };
      }
    }

    const updatedData = {
      checkpoints: finalCheckpoints,
      lastCheckpoint: updatedCheckpoints[lastCpId]
        ? {
            id: lastCpId,
            location: category.checkpoints.find(
              (cp) => cp.locationId === lastCpId
            )?.name || lastCpId,
            time: updatedCheckpoints[lastCpId].time,
          }
        : (editingRunner.lastCheckpoint || null),
      updatedAt: moment().tz(userTimeZone).toISOString(),
    };

    // Recalculate official timing fields after finish checkpoint edit.
    // This keeps Firestore consistent so the API cache reflects the correction.
    const penaltySeconds = parseInt(editForm.penaltySeconds) || 0;

    // A lap event has no `finish` checkpoint — its elapsed time comes from the
    // last lap. Without this, deleting L500 left gunTime frozen at the old
    // value, so the table still showed the time of a lap that no longer exists.
    if (category?.isLooping) {
      const lapParent = Object.values(finalCheckpoints).find(
        (c) => c && typeof c === "object" && Object.keys(c).some((k) => /^loop\d+$/.test(k))
      );
      const lastNum = lapParent
        ? Object.keys(lapParent).reduce((mx, k) => {
            const n = /^loop(\d+)$/.test(k) ? parseInt(k.slice(4), 10) : 0;
            return n > mx ? n : mx;
          }, 0)
        : 0;
      const lastTime = lastNum ? lapParent[`loop${lastNum}`]?.time : null;
      const flagOffM = category?.startTime
        ? moment(category.startTime.toDate ? category.startTime.toDate() : category.startTime).tz(userTimeZone)
        : null;
      if (lastTime && flagOffM?.isValid()) {
        const endM = moment(lastTime).tz(userTimeZone).add(penaltySeconds, "seconds");
        const ms = Math.max(0, endM.diff(flagOffM));
        const s = Math.floor(ms / 1000);
        const dur = `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}.${String(ms % 1000).padStart(3, "0")}`;
        updatedData.gunTime = dur;
        updatedData.finishTime = dur;
        updatedData.netTime = dur;
        updatedData.finishMatTime = lastTime;
        updatedData.completedLoops = lastNum;
        updatedData.lastCheckpoint = { id: "lap", time: lastTime, location: "Lap" };
      } else {
        updatedData.gunTime = null;
        updatedData.finishTime = null;
        updatedData.netTime = null;
        updatedData.finishMatTime = null;
        updatedData.completedLoops = 0;
      }
      updatedData.penaltySeconds = penaltySeconds;
      // Comma is the decimal separator in the exported sheets ("33,4").
      const extraRaw = String(editForm.extraMeters ?? "").replace(",", ".").trim();
      if (extraRaw === "") {
        updatedData.extraMeters = null;
      } else if (Number.isFinite(Number(extraRaw)) && Number(extraRaw) >= 0) {
        updatedData.extraMeters = Number(extraRaw);
      }
    }

    const finishEntry = finalCheckpoints['finish'];
    if (finishEntry?.time) {
      const rawFinishM = moment(finishEntry.time).tz(userTimeZone);
      // Apply penalty: effective finish = raw finish + penalty
      const finishM = penaltySeconds > 0
        ? rawFinishM.clone().add(penaltySeconds, 'seconds')
        : rawFinishM;
      const flagOff = category?.startTime
        ? moment(category.startTime.toDate ? category.startTime.toDate() : category.startTime).tz(userTimeZone)
        : null;
      // Prefer the Start checkpoint the operator just edited over the stored
      // startMatTime: the two must not drift apart, and the edited value is the
      // correction being made. Without this, fixing a wrong start in the editor
      // changed the displayed crossing but not the time it was measured from.
      const editedStart = updatedCheckpoints?.start?.time || null;
      const startSource = editedStart || editingRunner.startMatTime || null;
      const startMat = startSource
        ? moment(startSource.toDate ? startSource.toDate() : startSource).tz(userTimeZone)
        : null;
      // Keep the authoritative field in step with the edit, and release the
      // operator lock so a corrected start is not immediately frozen wrong.
      if (editedStart) {
        updatedData.startMatTime = editedStart;
        updatedData.missingStartMat = false;
      } else if (editingRunner.startMatTime && !updatedCheckpoints?.start?.time) {
        updatedData.startMatTime = null;
      }
      const calcDur = (from, to) => {
        if (!from?.isValid() || !to?.isValid() || !to.isAfter(from)) return null;
        const durMs = Math.round(to.diff(from));
        const durS = Math.floor(durMs / 1000);
        return `${String(Math.floor(durS/3600)).padStart(2,'0')}:${String(Math.floor((durS%3600)/60)).padStart(2,'0')}:${String(durS%60).padStart(2,'0')}.${String(durMs%1000).padStart(3,'0')}`;
      };
      const gunTime = calcDur(flagOff, finishM);
      const netTime = calcDur(startMat, finishM) || gunTime;
      const cat = state.categories.find(c => c.id === editingRunner.categoryId);
      const finishTime = cat?.useIndividualStart ? netTime : gunTime;
      // Always store penalty
      updatedData.penaltySeconds = penaltySeconds;
      // Comma is the decimal separator in the exported sheets ("33,4").
      const extraRaw = String(editForm.extraMeters ?? "").replace(",", ".").trim();
      if (extraRaw === "") {
        updatedData.extraMeters = null;
      } else if (Number.isFinite(Number(extraRaw)) && Number(extraRaw) >= 0) {
        updatedData.extraMeters = Number(extraRaw);
      }
      if (gunTime) {
        updatedData.finishMatTime = finishEntry.time; // raw (pre-penalty) finish
        updatedData.gunTime = gunTime;
        updatedData.netTime = netTime;
        updatedData.finishTime = finishTime || gunTime;
      } else {
        // If calculation fails (e.g. finish before start), clear the old stale data
        updatedData.finishMatTime = finishEntry.time || null;
        updatedData.gunTime = null;
        updatedData.netTime = null;
        updatedData.finishTime = null;
      }
    } else if (editingRunner.finishMatTime && !finalCheckpoints['finish']?.time) {
      // Finish checkpoint was cleared by editor — wipe official fields
      updatedData.finishMatTime = null;
      updatedData.gunTime = null;
      updatedData.netTime = null;
      updatedData.finishTime = null;
    }

    // Profile fields. Name/gender belong to the result doc; chipCode belongs to
    // the participant doc — the API merges the two when it serves results.
    const nextName = (editForm.name ?? "").trim();
    const nextGender = (editForm.gender ?? "").trim().toLowerCase();
    const nextChip = (editForm.chipCode ?? "").trim();
    const nextBib = String(editForm.bib ?? "").trim();

    if (nextName) updatedData.name = nextName;
    if (nextGender) updatedData.gender = nextGender;
    if (nextChip) updatedData.chipCode = nextChip;

    if (nextBib && nextBib !== String(editingRunner.bib)) {
      setState((prev) => ({
        ...prev,
        editError:
          "BIB cannot be changed here — the result document is keyed by BIB. Delete this row and re-enter it under the new BIB.",
      }));
      return;
    }

    try {
      await updateDoc(docRef, updatedData);

      if (requestedLapCorrection) {
        const correctionMoment = moment.tz(editForm.lapCorrectionTime, userTimeZone);
        if (requestedLapCorrection.want > requestedLapCorrection.have && !correctionMoment.isValid()) {
          throw new Error("A valid correction crossing time is required when adding loops");
        }
        const token = await getIdToken();
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/timing/correct-loop-count`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            eventId: EVENT_ID,
            bib: String(editingRunner.bib),
            targetCount: requestedLapCorrection.want,
            ...(requestedLapCorrection.want > requestedLapCorrection.have
              ? { correctionTime: correctionMoment.toISOString() }
              : {}),
          }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.message || "Loop correction failed");
      }

      // Mirror the editable profile onto the participant doc so the start list
      // and the chip→BIB lookup stay in step with what was just saved.
      if (nextChip || nextName || nextGender) {
        try {
          await setDoc(
            doc(db, "event_timing", EVENT_ID, "participants", String(editingRunner.bib)),
            {
              ...(nextName ? { name: nextName } : {}),
              ...(nextGender ? { gender: nextGender } : {}),
              ...(nextChip ? { chipCode: nextChip } : {}),
            },
            { merge: true }
          );
        } catch (participantErr) {
          // The result itself saved; surface the partial failure rather than
          // letting the operator believe the chip mapping was updated.
          setState((prev) => ({
            ...prev,
            editError: `Result saved, but participant record failed: ${participantErr.message}`,
          }));
          return;
        }
      }

      setState((prev) => ({
        ...prev,
        editingRunner: null,
        isDeleting: false,
        editForm: { checkpoints: {} },
        editSuccess: "Race result updated successfully!",
        editError: null,
        deleteError: null,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        editError: `Failed to update race result: ${error.message}`,
      }));
    }
  }, [state, userTimeZone, getIdToken, EVENT_ID]);

  const cancelEdit = useCallback(() => {
    setState((prev) => ({
      ...prev,
      editingRunner: null,
      isDeleting: false,
      editForm: { checkpoints: {} },
      editError: null,
      editSuccess: null,
      deleteError: null,
    }));
  }, []);

  const startDeleting = useCallback((runner) => {
    setState((prev) => ({
      ...prev,
      editingRunner: runner,
      isDeleting: true,
      editError: null,
      editSuccess: null,
      deleteError: null,
    }));
  }, []);

  const confirmDelete = useCallback(async () => {
    const { editingRunner } = state;
    if (!editingRunner) return;

    const db = getFirestore();
    const docRef = doc(db, "event_timing", EVENT_ID, "results", editingRunner.id);

    try {
      await deleteDoc(docRef);
      setState((prev) => ({
        ...prev,
        editingRunner: null,
        isDeleting: false,
        editForm: { checkpoints: {} },
        editSuccess: "Race result deleted successfully!",
        editError: null,
        deleteError: null,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        deleteError: `Failed to delete race result: ${error.message}`,
      }));
    }
  }, [state]);

  const cancelDelete = useCallback(() => {
    setState((prev) => ({
      ...prev,
      editingRunner: null,
      isDeleting: false,
      editForm: { checkpoints: {} },
      editError: null,
      editSuccess: null,
      deleteError: null,
    }));
  }, []);

  // Mark a participant DNS (or clear it) before any passing exists. Writes the
  // flag on the participant document, which every ingest path checks, so the
  // BIB is blocked rather than merely relabelled after the fact.
  const setParticipantDns = useCallback(async (runner, dns) => {
    // Every timing mutation is an operator action; the router rejects an
    // unauthenticated POST outright.
    const token = await getIdToken();
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}/api/timing/participant-dns`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ eventId: EVENT_ID, bib: String(runner.bib), dns }),
      }
    );
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      throw new Error(detail.message || `Request failed (${response.status})`);
    }
    setState((prev) => ({
      ...prev,
      participantsByBib: {
        ...prev.participantsByBib,
        [String(runner.bib)]: {
          ...(prev.participantsByBib[String(runner.bib)] || { bib: String(runner.bib) }),
          dns,
        },
      },
    }));
  }, [getIdToken, EVENT_ID]);

  // Update runner status (DNF/DNS/DSQ)
  const updateRunnerStatus = useCallback(async (runner, newStatus) => {
    const db = getFirestore();
    const docRef = doc(db, "event_timing", EVENT_ID, "results", runner.id);

    try {
      // A roster-only runner has no result document to carry a status. DNS is
      // the only meaningful mark before the start, so it is the only one
      // offered — and it is written to the participant instead.
      if (runner._rosterOnly) {
        await setParticipantDns(runner, newStatus === "DNS");
        setState((prev) => ({
          ...prev,
          editSuccess: newStatus === "DNS"
            ? `${runner.name} has been marked as DNS (did not start); their chip will no longer be counted.`
            : `DNS cleared for ${runner.name}; their chip will be counted again.`,
          editError: null,
        }));
        return;
      }

      // Keep the participant flag in step so the ingest guard and the published
      // status cannot disagree once a result already exists.
      if (newStatus === "DNS" || (runner.status === "DNS" && newStatus !== "DNS")) {
        await setParticipantDns(runner, newStatus === "DNS").catch(() => {
          // Non-fatal: the result status below is still the published truth.
        });
      }

      await updateDoc(docRef, {
        status: newStatus || null,
        updatedAt: moment().tz(userTimeZone).toISOString(),
      });
      const statusLabels = { DNF: "DNF", DNS: "DNS", DSQ: "Disqualified" };
      setState((prev) => ({
        ...prev,
        editSuccess: newStatus 
          ? `${runner.name} has been marked as ${statusLabels[newStatus] || newStatus}!`
          : `Status for ${runner.name} has been cleared!`,
        editError: null,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        editError: `Failed to update status: ${error.message}`,
      }));
    }
  }, [userTimeZone, setParticipantDns]);

  const handleNameClick = useCallback(
    (runner) => {
      const category = state.categories.find(
        (cat) => cat.id === runner.categoryId
      );
      navigate(
        `/${EVENT_ID}/results/${runner.bib}`,
        {
          state: {
            name: capitalizeWords(runner.name) || "-",
            ranking: runner.overallRank || "-",
            category: runner.categoryId || "-",
            bibNumber: runner.bib || "-",
            nationality: runner.nationality || "Indonesia",
            gender: runner.gender || "-",
            status: getRunnerStatus(runner, category),
            time: runner.checkpoints?.finish?.time || "-",
            duration:
              runner.finishDuration &&
              !isNaN(runner.finishDuration.split(":")[0])
                ? runner.finishDuration
                : "-",
            genderRanking: runner.genderRank || "-",
          },
        }
      );
    },
    [navigate, state.categories, getRunnerStatus]
  );

  if (state.error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <p className="text-red-500 font-oxanium">{state.error}</p>
      </div>
    );
  }

  if (state.loading) {
    return <PageLoader />;
  }

  return (
    <section className="min-h-screen mt-10 bg-white mb-10">
      <div className="container mx-auto px-4">
        <div className="mb-4 flex flex-wrap justify-between items-center gap-2">
          <h1 className="text-2xl font-bold text-slate-900">
            Results Editor - {state.eventName}
          </h1>
          <InputField
            type="text"
            value={bibNumberInput}
            onChange={handleInputChange}
            placeholder="Search BIB or Name"
            className="text-center shadow-sm"
            wrapperClassName="w-full md:w-64"
          />
        </div>
        
        <div className="mb-5 flex w-full flex-wrap items-center justify-between gap-x-6 gap-y-2">
          <div className="flex flex-wrap gap-2">
            {state.categories.map((category) => (
              <Button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                variant={state.selectedCategory === category.id ? "primary" : "secondary"}
                color="slate"
              >
                {category.name}
              </Button>
            ))}
            <Button
              onClick={() => handleCategoryChange("")}
              variant={state.selectedCategory === "" ? "primary" : "secondary"}
              color="slate"
            >
              All Categories
            </Button>
          </div>

          {/* Rows an official revisits. Without these the only way to find a
              penalised runner among 674 was to scroll the whole table. */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs font-medium uppercase tracking-wide text-slate-400">Show</span>
          {[
            ["all", "All"],
            ["penalty", "With penalty"],
            ["status", "DNF / DNS / DSQ"],
            ["nofinish", "No finish"],
          ].map(([key, label]) => {
            // Count over the same set the table renders — results plus the
            // roster entries that have not crossed a mat yet, narrowed to the
            // selected category — or the tallies would contradict the rows
            // underneath them. `rankedResults` holds every category (ranking
            // scopes to the selection, it does not filter), so the category
            // test has to be applied here.
            const countable = [...state.rankedResults, ...rosterOnlyRows].filter(
              (r) => state.selectedCategory === "" || r.categoryId === state.selectedCategory
            );
            const count =
              key === "all"
                ? countable.length
                : countable.filter((r) =>
                    key === "penalty"
                      ? (r.penaltySeconds || 0) > 0
                      : key === "status"
                        ? ["DNF", "DNS", "DSQ"].includes((r.status || "").toUpperCase())
                        : !r.checkpoints?.finish?.time && !r.finishMatTime
                  ).length;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFlagFilter(key)}
                className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  flagFilter === key
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {label}
                <span className={flagFilter === key ? "ml-1.5 text-slate-300" : "ml-1.5 text-slate-400"}>
                  {count}
                </span>
              </button>
            );
            })}
          </div>
        </div>

        {state.editSuccess && (
          <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-xl">
            {state.editSuccess}
          </div>
        )}
        {state.editError && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-xl">
            {state.editError}
          </div>
        )}
        {state.deleteError && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-xl">
            {state.deleteError}
          </div>
        )}

        {state.selectedCategory === "" ? (
          <div className="mb-4 p-4 bg-slate-50 rounded-card border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-3">Statistics - All Categories</h3>
            {/* Overall stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white p-3 rounded-card border border-slate-200">
                <p className="text-2xl font-bold text-slate-900">{filteredResults.length}</p>
                <p className="text-xs text-slate-500">Total Participants</p>
              </div>
              <div className="bg-white p-3 rounded-card border border-slate-200">
                <p className="text-2xl font-bold text-slate-900">
                  {filteredResults.filter(r => {
                      const cat = state.categories.find(c => c.id === r.categoryId);
                      if (cat?.isLooping) return state.eventStatus === "completed" && (r.completedLoops || 0) > 0;
                      return !!r.checkpoints?.finish?.time;
                  }).length}
                </p>
                <p className="text-xs text-slate-500">Finished</p>
              </div>
              <div className="bg-white p-3 rounded-card border border-slate-200">
                <p className="text-2xl font-bold text-slate-900">
                  {filteredResults.filter(r => {
                      if (r.status === "DNF" || r.status === "DNS" || r.status === "DSQ") return false;
                      const cat = state.categories.find(c => c.id === r.categoryId);
                      if (cat?.isLooping) return state.eventStatus !== "completed" || (r.completedLoops || 0) === 0;
                      return !r.checkpoints?.finish?.time;
                  }).length}
                </p>
                <p className="text-xs text-slate-500">Not Finished</p>
              </div>
              <div className="bg-white p-3 rounded-card border border-slate-200">
                <p className="text-2xl font-bold text-slate-900">
                  {filteredResults.filter(r => r.status === "DNF" || r.status === "DNS" || r.status === "DSQ").length}
                </p>
                <p className="text-xs text-slate-500">DNF/DNS/DSQ</p>
              </div>
            </div>
            {/* Per-category breakdown */}
            <div className="border-t border-slate-200 pt-3">
              <p className="text-sm font-medium text-slate-600 mb-2">Per Category:</p>
              <div className="overflow-hidden rounded-card border border-slate-200 bg-white">
                <div className="grid grid-cols-[minmax(180px,1.4fr)_repeat(4,minmax(72px,0.6fr))] items-center bg-slate-50 px-4 py-2 text-xs font-medium text-slate-500">
                  <span>Category</span>
                  <span className="text-center">Total</span>
                  <span className="text-center">Finish</span>
                  <span className="text-center">Not</span>
                  <span className="text-center">DNF</span>
                </div>
                {state.categories.map(cat => {
                  const catResults = filteredResults.filter(r => r.categoryId === cat.id);
                  const finished = cat.isLooping 
                    ? catResults.filter(r => state.eventStatus === "completed" && (r.completedLoops || 0) > 0).length
                    : catResults.filter(r => r.checkpoints?.finish?.time).length;
                  const notFinished = cat.isLooping
                    ? catResults.filter(r => (state.eventStatus !== "completed" || (r.completedLoops || 0) === 0) && !r.status).length
                    : catResults.filter(r => !r.checkpoints?.finish?.time && !r.status).length;
                  const statusCount = catResults.filter(r => r.status === "DNF" || r.status === "DNS" || r.status === "DSQ").length;
                  return (
                    <div key={cat.id} className="grid grid-cols-[minmax(180px,1.4fr)_repeat(4,minmax(72px,0.6fr))] items-center border-t border-slate-100 px-4 py-2.5 text-sm">
                      <p className="truncate font-semibold text-slate-900">{cat.name}</p>
                      <span className="text-center font-semibold tabular-nums text-slate-700">{catResults.length}</span>
                      <span className="text-center font-semibold tabular-nums text-green-600">{finished}</span>
                      <span className="text-center font-semibold tabular-nums text-orange-600">{notFinished}</span>
                      <span className={`text-center font-semibold tabular-nums ${statusCount > 0 ? "text-red-600" : "text-slate-400"}`}>
                        {statusCount}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <Top3Table 
              title="Top 3 Men" 
              results={state.top3Male} 
              userTimeZone={userTimeZone} 
            />
            <Top3Table 
              title="Top 3 Women" 
              results={state.top3Female} 
              userTimeZone={userTimeZone} 
            />
          </div>
        )}

        <div className="hidden overflow-x-auto pb-8 md:block">
          <table className="min-w-full border-separate table-auto" style={{ borderSpacing: "0 8px" }}>
            <thead>
              <tr>
                <th className="bg-slate-50 border-y border-l border-slate-200 rounded-l-card px-2 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-[50px]">
                  {state.selectedCategory === "" ? "#" : "Rank"}
                </th>
                <th className="bg-slate-50 border-y border-slate-200 px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider max-w-[150px] min-w-[180px]">
                  Name
                </th>
                <th className="bg-slate-50 border-y border-slate-200 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Bib
                </th>
                <th
                  className="bg-slate-50 border-y border-slate-200 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider max-w-[150px] cursor-pointer hover:bg-slate-100"
                  onClick={() => requestSort("gender")}
                >
                  Gender
                  {sortConfig.key === "gender" && (
                    <span className="ml-1">
                      {sortConfig.direction === "ascending" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
                <th className="bg-slate-50 border-y border-slate-200 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="bg-slate-50 border-y border-slate-200 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-[100px]">
                  Status
                </th>
                {isLoopCategory && (
                  <th className="bg-slate-50 border-y border-slate-200 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-[110px]">
                    Laps
                  </th>
                )}
                {tableCheckpoints.map((cp) => (
                  <th
                    key={cp.locationId}
                    className="bg-slate-50 border-y border-slate-200 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider"
                  >
                    {cp.name.toUpperCase()}
                  </th>
                ))}
                <th className="bg-slate-50 border-y border-slate-200 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Finish
                </th>
                <th className="bg-slate-50 border-y border-r border-slate-200 rounded-r-card px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-[80px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredResults.length === 0 ? (
                <tr>
                  <td
                    colSpan={8 + tableCheckpoints.length}
                    className="bg-white rounded-card border border-slate-200 px-4 py-6 text-center text-sm text-slate-600"
                  >
                    No race results available.
                  </td>
                </tr>
              ) : (
                filteredResults.map((result, index) => (
                  <tr
                    key={`${result.id || "unknown"}-${index}`}
                    className="hover:-translate-y-[2px] transition-transform shadow-sm group"
                  >
                    <td className="bg-white border-y border-slate-200 group-hover:bg-slate-50 transition-colors rounded-l-card border-l px-2 py-4 text-sm text-slate-600 text-center w-[60px]">
                      {state.selectedCategory === "" 
                        ? index + 1 
                        : (result.overallRank > 0 ? result.overallRank : "-")}
                    </td>
                    <td
                      className="bg-white border-y border-slate-200 group-hover:bg-slate-50 transition-colors px-4 py-4 whitespace-nowrap text-sm font-bold text-slate-900 uppercase max-w-[150px] truncate cursor-pointer hover:text-teal-600"
                      onClick={() => handleNameClick(result)}
                    >
                      {capitalizeWords(result.name) || "-"}
                    </td>
                    <td className="bg-white border-y border-slate-200 group-hover:bg-slate-50 transition-colors px-4 py-4 whitespace-nowrap text-sm text-slate-600 text-center">
                      <span className="border border-slate-200 rounded-full px-2 py-1">
                        {result.bib ? `BIB ${result.bib}` : "-"}
                      </span>
                    </td>
                    <td className="bg-white border-y border-slate-200 group-hover:bg-slate-50 transition-colors px-4 py-4 whitespace-nowrap text-sm text-slate-600 text-center max-w-[150px]">
                      <span className="border border-slate-200 rounded-full px-2 py-1">
                        {formatGender(result.gender)}
                      </span>
                    </td>
                    <td className="bg-white border-y border-slate-200 group-hover:bg-slate-50 transition-colors px-4 py-4 whitespace-nowrap text-sm font-mono text-slate-500 text-center">
                      {result.chipCode || state.participantsByBib[String(result.bib)]?.chipCode || "-"}
                    </td>
                    <td className="bg-white border-y border-slate-200 group-hover:bg-slate-50 transition-colors px-2 py-4 whitespace-nowrap text-sm text-center">
                      <select
                        value={result.status || ""}
                        onChange={(e) => updateRunnerStatus(result, e.target.value)}
                        className={`border rounded px-2 py-1 text-xs font-medium ${
                          result.status === "DNF" ? "border-orange-400 text-orange-600 bg-orange-50" :
                          result.status === "DNS" ? "border-slate-400 text-slate-600 bg-slate-50" :
                          result.status === "DSQ" ? "border-red-400 text-red-600 bg-red-50" :
                          "border-slate-200 text-slate-500 bg-white"
                        }`}
                      >
                        <option value="">-</option>
                        {/* A runner with no crossing yet can only be a no-show;
                            DNF and DSQ need a race that actually started. */}
                        {!result._rosterOnly && <option value="DNF">DNF</option>}
                        <option value="DNS">DNS</option>
                        {!result._rosterOnly && <option value="DSQ">DSQ</option>}
                        {/* Finished, but past the cut-off: a real result that
                            does not place. Distinct from DNF, where the runner
                            stopped. */}
                        {!result._rosterOnly && <option value="COT">OVER COT</option>}
                      </select>
                    </td>
                    {isLoopCategory && (() => {
                      // Read the summary computed at load — the lap entries
                      // themselves are no longer held in memory.
                      const highest = result._lapHighest || 0;
                      const gaps = result._lapGaps || 0;
                      const adjusted = !!result._lapAdjusted;
                      return (
                        <td className="bg-white border-y border-slate-200 group-hover:bg-slate-50 transition-colors px-4 py-4 text-center text-sm whitespace-nowrap">
                          <span className="font-semibold text-slate-700 tabular-nums">{highest || "-"}</span>
                          {gaps > 0 && (
                            <span
                              title={`${gaps} lap number(s) missing from the sequence`}
                              className="ml-1.5 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"
                            >
                              {gaps} gap
                            </span>
                          )}
                          {adjusted && (
                            <span
                              title="Lap count was corrected by hand"
                              className="ml-1.5 rounded-full border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-500"
                            >
                              adj
                            </span>
                          )}
                        </td>
                      );
                    })()}
                    {formatResults(result).map((entry, idx) => (
                      <td
                        key={idx}
                        className={`bg-white border-y border-slate-200 group-hover:bg-slate-50 transition-colors px-4 py-4 text-center text-sm ${
                          entry.isCOT ? "text-red-600" : 
                          entry.isDNF ? "text-orange-600 font-semibold" : 
                          "text-slate-600"
                        } whitespace-nowrap`}
                      >
                        {entry.time}
                      </td>
                    ))}
                    <td className={`bg-white border-y border-slate-200 group-hover:bg-slate-50 transition-colors px-4 py-4 text-center text-sm font-semibold whitespace-nowrap ${
                        result.isCOT ? "text-red-600" : "text-slate-600"
                      }`}>
                      {(() => {
                        const cat = state.categories.find(c => c.id === result.categoryId);
                        const finishCp = cat?.checkpoints?.find(c => c.type === "startfinish" || c.locationId === "finish");
                        // A lap event has no `finish` checkpoint — the elapsed
                        // time comes from the last lap — so this column read
                        // empty for every looping runner.
                        // Same shared resolver as the mobile card below, so the
                        // desktop table cannot disagree with it. A lap category
                        // still reports progress by lap count rather than a
                        // finish time.
                        const hasFinish = cat?.isLooping
                          ? (result._lapHighest || result.completedLoops || 0) > 0
                          : finishDisplay(result, cat) !== "-";
                        if (!hasFinish) {
                          return result.status === "DNF" ? "DNF"
                            : result.status === "DNS" ? "DNS"
                            : result.status === "DSQ" ? "DSQ"
                            : "-";
                        }
                        const dur = cat?.isLooping
                          ? ((result.finishDuration && result.finishDuration !== "-")
                              ? result.finishDuration
                              : (result.gunTime || result.netTime || result.finishTime || "-")
                            ).replace(/\.\d+$/, "")
                          : finishDisplay(result, cat);
                        const penalty = result.penaltySeconds > 0 ? result.penaltySeconds : 0;
                        // Raw mat time, before the penalty was added. Without it the
                        // operator cannot tell what was actually clocked — only the
                        // adjusted figure was on screen.
                        const rawMat = penalty > 0 && result.finishMatTime && cat?.startTime
                          ? (() => {
                              const f = moment(result.finishMatTime.toDate ? result.finishMatTime.toDate() : result.finishMatTime);
                              const s = moment(cat.startTime.toDate ? cat.startTime.toDate() : cat.startTime);
                              if (!f.isValid() || !s.isValid() || !f.isAfter(s)) return null;
                              const t = Math.floor(f.diff(s) / 1000);
                              return `${String(Math.floor(t / 3600)).padStart(2, "0")}:${String(Math.floor((t % 3600) / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
                            })()
                          : null;
                        return (
                          <span className="inline-flex flex-col items-center gap-1">
                            <span className="tabular-nums">{dur}</span>
                            {penalty > 0 && (
                              <span className="inline-flex items-center gap-1.5 text-sm leading-5">
                                {rawMat && (
                                  <span className="tabular-nums font-normal text-slate-500" title="Original finish time">
                                    {rawMat}
                                  </span>
                                )}
                                <span className="text-slate-300">+</span>
                                <span className="font-semibold text-amber-600" title="Time penalty">
                                  {formatPenalty(penalty)}
                                </span>
                              </span>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="bg-white border-y border-r border-slate-200 rounded-r-card group-hover:bg-slate-50 transition-colors px-2 py-4 whitespace-nowrap text-sm text-slate-600 text-center">
                      {/* Roster-only rows have no result document, so there is
                          nothing to edit or delete — only the DNS mark applies. */}
                      {result._rosterOnly ? (
                        <span className="text-xs text-slate-400" title="No timing data recorded yet">
                          not started
                        </span>
                      ) : (
                        <>
                          <button
                            onClick={() => startEditing(result)}
                            className="text-blue-600 hover:text-blue-800 mr-2"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => startDeleting(result)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card view follows the public Results page pattern while
            retaining editor-only status, checkpoint, and action controls. */}
        <div className="space-y-3 pb-6 md:hidden">
          {filteredResults.length === 0 ? (
            <div className="rounded-card border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
              No race results available.
            </div>
          ) : (
            filteredResults.map((result, index) => {
              const category = state.categories.find(c => c.id === result.categoryId);
              const checkpointEntries = formatResults(result);
              // One shared resolver decides what a finish is, for every timing
              // method. Each page used to work this out from its own reading of
              // `checkpoints`, which is why the same bug had to be fixed here,
              // in Results, in the venue board and in the export separately.
              const penalty = result.penaltySeconds > 0 ? result.penaltySeconds : 0;
              const resolvedFinish = finishDisplay(result, category);
              const finishValue = resolvedFinish === "-" ? (result.status || "-") : resolvedFinish;
              const rawMat = penalty > 0 && result.finishMatTime && category?.startTime
                ? (() => {
                    const finishMoment = moment(result.finishMatTime.toDate ? result.finishMatTime.toDate() : result.finishMatTime);
                    const startMoment = moment(category.startTime.toDate ? category.startTime.toDate() : category.startTime);
                    if (!finishMoment.isValid() || !startMoment.isValid() || !finishMoment.isAfter(startMoment)) return null;
                    const seconds = Math.floor(finishMoment.diff(startMoment) / 1000);
                    return `${String(Math.floor(seconds / 3600)).padStart(2, "0")}:${String(Math.floor((seconds % 3600) / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
                  })()
                : null;

              return (
                <div
                  key={`mobile-${result.id || "unknown"}-${index}`}
                  className="overflow-hidden rounded-card border border-slate-200 bg-white p-3 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <span className="inline-flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 px-1.5 text-xs font-bold text-slate-700">
                      {state.selectedCategory === ""
                        ? index + 1
                        : (result.overallRank > 0 ? result.overallRank : "-")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <button
                        type="button"
                        onClick={() => handleNameClick(result)}
                        className="block max-w-full truncate text-left text-sm font-bold uppercase text-slate-900"
                      >
                        {capitalizeWords(result.name) || "-"}
                      </button>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                        <span>BIB {result.bib || "-"}</span>
                        <span>{formatGender(result.gender)}</span>
                        <span className="font-mono">
                          {result.chipCode || state.participantsByBib[String(result.bib)]?.chipCode || "-"}
                        </span>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className={`text-sm font-semibold tabular-nums ${
                        result.status === "DNF" ? "text-orange-600" :
                        result.status === "DSQ" ? "text-red-600" :
                        result.status === "DNS" ? "text-slate-500" :
                        result.isCOT ? "text-red-600" : "text-slate-700"
                      }`}>
                        {finishValue}
                      </p>
                      {penalty > 0 && (
                        <p className="mt-1 text-xs tabular-nums text-slate-500">
                          {rawMat && <span>{rawMat} <span className="text-slate-300">+</span> </span>}
                          <span className="font-semibold text-amber-600">{formatPenalty(penalty)}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {tableCheckpoints.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3">
                      {checkpointEntries.map((entry, checkpointIndex) => (
                        <div
                          key={tableCheckpoints[checkpointIndex]?.locationId || checkpointIndex}
                          className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 py-2"
                        >
                          <span className="truncate text-[11px] font-medium uppercase text-slate-400">
                            {tableCheckpoints[checkpointIndex]?.name || `CP ${checkpointIndex + 1}`}
                          </span>
                          <span className={`shrink-0 text-xs font-medium tabular-nums ${
                            entry.isCOT ? "text-red-600" :
                            entry.isDNF ? "text-orange-600" : "text-slate-600"
                          }`}>
                            {entry.time}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                    <select
                      value={result.status || ""}
                      onChange={(event) => updateRunnerStatus(result, event.target.value)}
                      aria-label={`Status ${capitalizeWords(result.name)}`}
                      className={`rounded-lg border px-2.5 py-2 text-xs font-medium ${
                        result.status === "DNF" ? "border-orange-400 bg-orange-50 text-orange-600" :
                        result.status === "DNS" ? "border-slate-400 bg-slate-50 text-slate-600" :
                        result.status === "DSQ" ? "border-red-400 bg-red-50 text-red-600" :
                        "border-slate-200 bg-white text-slate-500"
                      }`}
                    >
                      <option value="">Status</option>
                      {!result._rosterOnly && <option value="DNF">DNF</option>}
                      <option value="DNS">DNS</option>
                      {!result._rosterOnly && <option value="DSQ">DSQ</option>}
                      {!result._rosterOnly && <option value="COT">OVER COT</option>}
                    </select>
                    <div className="flex items-center gap-1">
                      {result._rosterOnly ? (
                        <span className="text-xs text-slate-400">not started</span>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => startEditing(result)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-blue-600 hover:bg-blue-50"
                            title="Edit"
                            aria-label={`Edit ${capitalizeWords(result.name)}`}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            type="button"
                            onClick={() => startDeleting(result)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-600 hover:bg-red-50"
                            title="Delete"
                            aria-label={`Delete ${capitalizeWords(result.name)}`}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {state.editingRunner && !state.isDeleting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4 ">
            <div className="bg-white rounded-card border border-slate-200 p-4 w-full max-w-2xl max-h-[92vh] overflow-y-auto">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Edit Runner: {capitalizeWords(state.editingRunner.name)} (BIB{" "}
                {state.editingRunner.bib})
              </h2>
              {/* Runner profile — name/gender live on the result doc, chipCode on
                  the participant doc; both are written on save. */}
              <div className="mb-4 grid grid-cols-1 gap-4">
                {/* Name - full width */}
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Name</label>
                  <InputField
                    type="text"
                    value={state.editForm.name ?? ""}
                    onChange={(e) => handleEditFieldChange("name", e.target.value)}
                  />
                </div>
                {/* BIB | Gender | Code — 3 columns */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">BIB</label>
                    <InputField
                      type="text"
                      inputMode="numeric"
                      value={state.editForm.bib ?? ""}
                      onChange={(e) => handleEditFieldChange("bib", e.target.value.replace(/\D/g, ""))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Gender</label>
                    <select
                      value={state.editForm.gender ?? ""}
                      onChange={(e) => handleEditFieldChange("gender", e.target.value)}
                      className="w-full h-[42px] px-3 border border-slate-200 rounded-lg bg-white text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400"
                    >
                      <option value="">-</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Code</label>
                    <InputField
                      type="text"
                      value={state.editForm.chipCode ?? ""}
                      onChange={(e) => handleEditFieldChange("chipCode", e.target.value.trim())}
                    />
                  </div>
                </div>
              </div>

              {state.editError && (
                <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
                  {state.editError}
                </div>
              )}
              <div className="mb-4 max-h-[55vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                {(() => {
                  const runnerCat = state.categories.find((cat) => cat.id === state.editingRunner.categoryId);
                  if (!runnerCat) return null;
                  // Use full category checkpoints (including startfinish) for the modal
                  // Lap rows come only from what the runner recorded.
                  const recordedLaps = Object.keys(state.editForm.checkpoints || {})
                    .map((k) => (/^loop(\d+)$/.test(k) ? parseInt(k.slice(4), 10) : 0))
                    .reduce((mx, n) => (n > mx ? n : mx), 0);
                  const lapRows = recordedLaps;
                  // A buzzer-timed category defines no checkpoints, so the list
                  // below is empty and the modal had nothing to edit at all.
                  // Its finish is a duration, entered directly.
                  if (runnerCat.isBuzzerTimed) {
                    return (
                      <div className="col-span-2 p-4 bg-slate-50 rounded-card border border-slate-200">
                        <label className="block text-sm font-medium text-slate-600 mb-1">
                          Finish time
                        </label>
                        <div className="flex gap-2 items-center">
                          <InputField
                            type="text"
                            placeholder="00:05:00"
                            value={state.editForm.recordedDuration || ""}
                            onChange={(e) =>
                              setState((prev) => ({
                                ...prev,
                                editForm: { ...prev.editForm, recordedDuration: e.target.value },
                              }))
                            }
                            wrapperClassName="flex-1"
                          />
                          <button
                            onClick={() =>
                              setState((prev) => ({
                                ...prev,
                                editForm: { ...prev.editForm, recordedDuration: "" },
                              }))
                            }
                            className="p-2 text-slate-500 hover:text-slate-900 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shrink-0"
                            title="Clear time"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                        <p className="mt-2 text-xs text-slate-500">
                          Elapsed time from the buzzer clock, as hh:mm:ss
                        </p>
                      </div>
                    );
                  }

                  const modalCheckpoints = runnerCat.isLooping
                    ? Array.from({ length: Math.max(1, lapRows) }, (_, i) => ({
                        locationId: `loop${i + 1}`,
                        name: `L${i + 1}`,
                      }))
                    : (runnerCat.checkpoints || []).filter((cp) => cp.locationId && cp.name);
                  return modalCheckpoints.flatMap((cp) => {
                    // startfinish type → expand to two separate fields: Start and Finish
                    if (cp.type === "startfinish") {
                      return [
                        { ...cp, _editKey: "start", _label: "Start" },
                        { ...cp, _editKey: "finish", _label: "Finish" },
                      ];
                    }
                    return [{ ...cp, _editKey: cp.locationId, _label: cp.name }];
                  }).map((cp) => {
                    const editKey = cp._editKey;
                    const label = cp._label;
                    const currentTime = state.editForm.checkpoints[editKey]?.time || "";
                    let datetimeValue = "";
                    if (currentTime) {
                      if (currentTime.includes("T")) {
                        datetimeValue = currentTime.slice(0, 19);
                      } else {
                        const dateStr = state.eventDate || moment().format("YYYY-MM-DD");
                        const timeVal = currentTime.length === 5 ? `${currentTime}:00` : currentTime;
                        datetimeValue = `${dateStr}T${timeVal}`;
                      }
                    }
                    return (
                      <div key={`${cp.locationId}-${editKey}`} className="p-4 bg-slate-50 rounded-card border border-slate-200">
                        <label className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-600 mb-1">
                          {label}
                          {/* Where a start came from decides whether it is worth
                              correcting: an operator time is the wave press,
                              accurate to when the group was sent off, while a mat
                              time is that runner's own crossing. */}
                          {editKey === "start" && state.editingRunner?.startFromOperator && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                              operator start
                            </span>
                          )}
                          {editKey === "start" &&
                            state.editingRunner?.startLocked &&
                            !state.editingRunner?.startFromOperator && (
                              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
                                mat · locked
                              </span>
                            )}
                        </label>
                        <div className="flex gap-2 items-center">
                            <InputField
                              type="datetime-local"
                              step="1"
                              value={datetimeValue}
                              onChange={(e) => handleEditInputChange(editKey, e.target.value)}
                              wrapperClassName="flex-1"
                            />
                            <button
                                onClick={() => handleEditInputChange(editKey, "")}
                                className="p-2 text-slate-500 hover:text-slate-900 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shrink-0"
                                title="Clear Time"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                      </div>
                    );
                   })
                })()}
                </div>
              </div>

              {/* Lap correction for looping categories.
                  Lap numbers come from the order reads arrive, so a chip the mat
                  misses costs that lap permanently and the system cannot tell.
                  Over ~500 crossings in 24 hours that will happen, and the count
                  is what decides the placing, so an official needs to be able to
                  set it against the marshal's tally. */}
              {(() => {
                const cat = state.categories.find((c) => c.id === state.editingRunner?.categoryId);
                if (!cat?.isLooping) return null;
                const cps = state.editingRunner?.checkpoints || {};
                const ownRecorded = Object.values(cps).reduce((mx, c) => {
                  if (!c || typeof c !== "object") return mx;
                  return mx + Object.keys(c).filter((key) => /^loop\d+$/.test(key) && c[key]?.time).length;
                }, 0);
                const recorded = cat.isRelay && state.editingRunner?.teamId
                  ? state.raceResults
                      .filter((runner) => runner.categoryId === state.editingRunner.categoryId && runner.teamId === state.editingRunner.teamId)
                      .reduce((count, runner) => count + (Number(runner._lapHighest) || Number(runner.completedLoops) || 0), 0)
                  : ownRecorded;
                const val = state.editForm.lapAdjustTo ?? recorded;
                const diff = val - recorded;
                return (
                  <div className="p-4 bg-slate-50 rounded-card border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-semibold text-slate-700">{cat.isRelay ? "Team loop count" : "Loop count"}</label>
                      <span className="text-xs text-slate-400">
                        Recorded by the mat: <span className="font-semibold text-slate-600">{recorded}</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditFieldChange("lapAdjustTo", Math.max(0, val - 1))}
                        className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      >
                        −
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={val}
                        onChange={(e) =>
                          handleEditFieldChange(
                            "lapAdjustTo",
                            Math.max(0, parseInt(e.target.value.replace(/\D/g, ""), 10) || 0)
                          )
                        }
                        className="h-9 w-24 px-2 text-center border border-slate-200 rounded-lg text-sm tabular-nums"
                      />
                      <button
                        type="button"
                        onClick={() => handleEditFieldChange("lapAdjustTo", val + 1)}
                        className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      >
                        +
                      </button>
                      {diff !== 0 && (
                        <span className={`text-xs font-semibold ${diff > 0 ? "text-teal-700" : "text-amber-700"}`}>
                          {diff > 0 ? `+${diff}` : diff} lap
                        </span>
                      )}
                    </div>
                    {diff > 0 && (
                      <div className="mt-3 max-w-xs">
                        <InputField
                          type="datetime-local"
                          step="1"
                          label="Last corrected crossing"
                          value={state.editForm.lapCorrectionTime || ""}
                          onChange={(event) => handleEditFieldChange("lapCorrectionTime", event.target.value)}
                        />
                      </div>
                    )}
                    <p className="mt-2 text-[11px] leading-snug text-slate-500">
                      Existing mat crossings remain unchanged. Adding loops requires the actual time of the last corrected crossing; removing loops trims only the tail. Relay corrections update the shared team total and member contributions together.
                    </p>
                  </div>
                );
              })()}

              {/* Penalty Section */}
              <div className="p-4 bg-slate-50 rounded-card border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-amber-700">Penalty</label>
                  <span className="text-xs text-slate-400">Automatically added to finish time on save.</span>
                </div>
                {/* What the mat actually clocked, and what the penalty makes it.
                    Editing a penalty blind — seeing only the adjusted figure —
                    is how a correction gets applied twice. */}
                {(() => {
                  const p = parseInt(state.editForm.penaltySeconds) || 0;
                  const cat = state.categories.find((c) => c.id === state.editingRunner?.categoryId);
                  const rawIso = state.editingRunner?.finishMatTime;
                  if (!rawIso || !cat?.startTime) return null;
                  const f = moment(rawIso.toDate ? rawIso.toDate() : rawIso);
                  const s = moment(cat.startTime.toDate ? cat.startTime.toDate() : cat.startTime);
                  if (!f.isValid() || !s.isValid() || !f.isAfter(s)) return null;
                  const fmt = (ms) => {
                    const t = Math.floor(ms / 1000);
                    return `${String(Math.floor(t / 3600)).padStart(2, "0")}:${String(Math.floor((t % 3600) / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
                  };
                  const base = f.diff(s);
                  return (
                    <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                      <span className="text-slate-500">Mat time</span>
                      <span className="font-semibold text-slate-700 tabular-nums">{fmt(base)}</span>
                      {p > 0 && (
                        <>
                          <span className="text-slate-400">+ {formatPenalty(p)} →</span>
                          <span className="font-semibold text-amber-700 tabular-nums">{fmt(base + p * 1000)}</span>
                        </>
                      )}
                    </div>
                  );
                })()}
                <div className="grid grid-cols-2 gap-4">
                  {/* Minutes */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Minutes</label>
                    <InputField
                      type="number"
                      min="0"
                      step="1"
                      placeholder="0"
                      value={Math.floor((state.editForm.penaltySeconds ?? 0) / 60)}
                      onChange={(e) => {
                        const mins = Math.max(0, parseInt(e.target.value) || 0);
                        const secs = (state.editForm.penaltySeconds ?? 0) % 60;
                        handleEditFieldChange("penaltySeconds", mins * 60 + secs);
                      }}
                      className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </div>
                  {/* Seconds */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Seconds</label>
                    <InputField
                      type="number"
                      min="0"
                      max="59"
                      step="1"
                      placeholder="0"
                      value={(state.editForm.penaltySeconds ?? 0) % 60}
                      onChange={(e) => {
                        const secs = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                        const mins = Math.floor((state.editForm.penaltySeconds ?? 0) / 60);
                        handleEditFieldChange("penaltySeconds", mins * 60 + secs);
                      }}
                      className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </div>
                </div>
                {(state.editForm.penaltySeconds > 0) && (
                  <p className="mt-2 text-xs text-amber-700 font-semibold">
                    Total: {state.editForm.penaltySeconds}s
                  </p>
                )}
              </div>

              {/* Extra distance — timed categories on a measured lane only. The
                  clock stops wherever the runner is, and an official paces out
                  the part-lap; no mat can report it, so it is typed here. */}
              {Number(
                state.categories.find((c) => c.id === state.editingRunner?.categoryId)
                  ?.lapDistanceMeters,
              ) > 0 && (
                <div className="mt-4 p-4 bg-slate-50 rounded-card border border-slate-200">
                  <label className="block text-sm font-medium text-slate-600 mb-1">
                    Extra distance past the last lap
                  </label>
                  <div className="flex items-center gap-2">
                    <InputField
                      type="text"
                      inputMode="decimal"
                      placeholder="33.4"
                      value={state.editForm.extraMeters ?? ""}
                      onChange={(e) =>
                        handleEditFieldChange(
                          "extraMeters",
                          e.target.value.replace(",", ".").replace(/[^\d.]/g, ""),
                        )
                      }
                      wrapperClassName="w-32"
                    />
                    <span className="text-xs text-slate-500">metres</span>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Leave blank if the runner did not finish the time — a DNF has
                    no measured distance.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 mt-4">
                <Button onClick={saveEdit} variant="primary" color="slate">
                  Save
                </Button>
                <Button onClick={cancelEdit} variant="secondary" color="slate">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {state.editingRunner && state.isDeleting && state.deleteError && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h2 className="text-lg font-semibold text-red-600 mb-4">
                Delete Runner: {capitalizeWords(state.editingRunner.name)} (BIB{" "}
                {state.editingRunner.bib})
              </h2>
              <p className="mb-4 text-red-700">{state.deleteError}</p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 bg-slate-300 text-slate-600 rounded hover:bg-slate-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {state.editingRunner && state.isDeleting && !state.deleteError && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h2 className="text-lg font-semibold text-red-600 mb-4">
                Confirm Delete Runner:{" "}
                {capitalizeWords(state.editingRunner.name)} (BIB{" "}
                {state.editingRunner.bib})
              </h2>
              <p className="mb-4">
                Are you sure you want to delete this runner's results?
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 bg-slate-300 text-slate-600 rounded hover:bg-slate-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ResultsEditor;
